<script lang="ts">
  import type { Class } from '$lib/api/types';
  import QuickSearch from '../common/QuickSearch.svelte';
  import { listCourses } from '$lib/api/courses';
  import DatePicker from '$lib/components/common/DatePicker.svelte';
  import { Datepicker } from 'vanillajs-datepicker';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faLoader } from '@itdev-fortawesome/pro-solid-svg-icons/faLoader';
  import { formatDateShort } from '$lib/util';
  import Alert from '../common/bootstrap/Alert.svelte';
  import Label from '../common/bootstrap/Label.svelte';
  import Input from '../common/bootstrap/Input.svelte';
  import { auth } from '$lib/stores/auth.svelte';

  interface Props {
    classInfo?: Class;
    onSave?: (clazz: Pick<Class, 'name' | 'courseId' | 'startDate' | 'endDate'>) => Promise<void>;
  }

  let { classInfo, onSave }: Props = $props();

  let className = $state(classInfo?.name ?? '');
  let courseValue: string | null = $state(classInfo?.courseId.toString() ?? '');
  let startDate = $state(classInfo ? formatDateShort(classInfo.startDate) : '');
  let endDate = $state(classInfo ? formatDateShort(classInfo.endDate) : '');

  let classNameTouched = $state(false);
  let courseTouched = $state(false);
  let startDateTouched = $state(false);
  let endDateTouched = $state(false);

  let startDateValid = $derived.by(() => {
    if (startDate.length === 0) return false;
    if (endDate.length === 0) return true;
    const startDateParsed = Datepicker.parseDate(startDate, 'mm/dd/yyyy');
    const endDateParsed = Datepicker.parseDate(endDate, 'mm/dd/yyyy');
    return startDateParsed < endDateParsed;
  });

  let endDateValid = $derived.by(() => {
    if (endDate.length === 0) return false;
    if (startDate.length === 0) return true;
    const startDateParsed = Datepicker.parseDate(startDate, 'mm/dd/yyyy');
    const endDateParsed = Datepicker.parseDate(endDate, 'mm/dd/yyyy');
    return startDateParsed < endDateParsed;
  });

  let saving = $state(false);
  let formErrorMessage: string | null = $state(null);

  function validate() {
    if (
      className.length > 0 &&
      className.length <= 256 &&
      courseValue != null &&
      courseValue.length > 0 &&
      startDateValid &&
      endDateValid
    ) {
      return true;
    }
    classNameTouched = true;
    courseTouched = true;
    startDateTouched = true;
    endDateTouched = true;
    return false;
  }

  async function formSubmit(e: SubmitEvent) {
    e.preventDefault();
    if (!auth.isAdmin) {
      return;
    }

    if (!onSave) {
      throw new Error('onSave not provided but user is admin');
    }

    if (!validate()) {
      formErrorMessage = 'Please fix the validation errors and try again.';
      return;
    }

    saving = true;
    formErrorMessage = null;
    try {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);

      await onSave({
        name: className,
        courseId: parseInt(courseValue!),
        startDate: startDateObj.toISOString(),
        endDate: endDateObj.toISOString(),
      });
    } catch (e) {
      if (e instanceof Error) {
        formErrorMessage = e.message;
      } else {
        formErrorMessage = `${e}`;
      }

      saving = false;
      throw e;
    }
  }
</script>

<form onsubmit={formSubmit}>
  {#if formErrorMessage}
    <Alert color="danger">
      {formErrorMessage}
    </Alert>
  {/if}
  <div class="row row-cols-1 row-cols-md-2">
    <div class="mb-3">
      <Label for="class_name" class="is-required">Class Name</Label>
      <Input
        id="class_name"
        bind:value={className}
        placeholder="e.g. NURS 31165-001 Spring 2026"
        required
        readonly={!auth.isAdmin}
        maxlength={256}
        onblur={() => (classNameTouched = true)}
        feedback="Class Name is required and must be 256 characters or less"
        invalid={classNameTouched && (className.length === 0 || className.length > 256)}
      />
    </div>
    <div class="mb-3">
      <Label for="course_id" class="is-required">Course</Label>
      {#if auth.isAdmin}
        <QuickSearch
          placeholder="Select Course"
          id="course_id"
          bind:value={courseValue}
          invalid={courseTouched && (courseValue === null || courseValue.length === 0)}
          feedback="Please choose a course."
          valueText={classInfo ? `${classInfo.course.code} — ${classInfo.course.name}` : ''}
          minimumQueryLength={0}
          loadData={async (query, page, pageSize, signal) => {
            const courses = await listCourses(
              // show full list instead of search query if a value is set
              courseValue !== null && courseValue.length > 0 ? '' : query,
              page,
              pageSize,
              signal,
            );
            return courses.map(c => {
              return {
                value: c.id.toString(),
                text: `${c.code} — ${c.name}`,
                title: c.code,
                subtitle: c.name,
              };
            });
          }}
          bind:touched={courseTouched}
          emptySearchResultsLabel="No courses found"
        />
      {:else}
        <!-- classInfo should not be null here because the /new path is not accessible for instructors -->
        <Input id="course_id" readonly value={`${classInfo!.course.code} - ${classInfo!.course.name}`} />
      {/if}
    </div>
    <div class="mb-3">
      <Label for="start_date" class="is-required">Start Date</Label>
      <DatePicker
        placeholder="Select start date"
        id="start_date"
        invalid={startDateTouched && !startDateValid}
        aria-errormessage={startDateTouched && !startDateValid ? 'start_date_invalid_feedback' : undefined}
        aria-describedby="start_date_help_text"
        bind:value={startDate}
        readonly={!auth.isAdmin}
        onblur={() => (startDateTouched = true)}
      />
      <div id="start_date_invalid_feedback" class="invalid-feedback">
        Start date is required and must be a date before end date.
      </div>
      <div id="start_date_help_text" class="form-text">
        The class leaderboards will open and students will be able to start logging skills at the start of this day.
      </div>
    </div>
    <div class="mb-3">
      <Label for="end_date" class="is-required">End Date</Label>
      <DatePicker
        placeholder="Select end date"
        id="end_date"
        invalid={endDateTouched && !endDateValid}
        aria-errormessage={endDateTouched && !endDateValid ? 'end_date_invalid_feedback' : undefined}
        aria-describedby="end_date_help_text"
        bind:value={endDate}
        readonly={!auth.isAdmin}
        minDate={startDate}
        onblur={() => (endDateTouched = true)}
      />
      <div id="end_date_invalid_feedback" class="invalid-feedback">
        End date is required and must be a date before end date.
      </div>
      <div id="end_date_help_text" class="form-text">
        The class leaderboards will close and students will no longer be able to log skills at the end of this day.
      </div>
    </div>
  </div>
  {#if auth.isAdmin}
    <div class="text-end">
      <button type="submit" class="btn btn-primary" disabled={saving}>
        {#if saving}
          <FontAwesomeIcon icon={faLoader} spin class="me-2" />Saving...
        {:else}
          Save
        {/if}
      </button>
    </div>
  {/if}
</form>
